/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */
import {
  Box,
  Chip,
  ListItem,
  ListItemButton,
  Tooltip,
  Typography,
} from '@mui/material';
import React from 'react';
import { usePluginsTranslation } from '../../../utils/i18n';
import type { DebugLogEntry } from '../pluginDebugTypes';
import { PreBox } from './PreBox';

const sourceColor = (source: DebugLogEntry['source']): string =>
  source === 'geoweb' ? '#1976d2' : '#9c27b0';

const typeLabel = (
  type: DebugLogEntry['type'],
  t: (key: string) => string,
): string => {
  if (type === 'scope-update') {
    return t('plugin-debug-type-state');
  }
  if (type === 'internal-state') {
    return t('plugin-debug-type-internal');
  }
  return t('plugin-debug-type-event');
};

const formatTime = (iso: string): string => {
  try {
    const date = new Date(iso);
    return date.toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      fractionalSecondDigits: 3,
    });
  } catch {
    return iso;
  }
};

const typeColor = (
  type: DebugLogEntry['type'],
): 'info' | 'warning' | 'default' => {
  if (type === 'scope-update') {
    return 'info';
  }
  if (type === 'internal-state') {
    return 'default';
  }
  return 'warning';
};

export type EntryRowVariant = 'activity' | 'recent';

interface EntryRowProps {
  entry: DebugLogEntry;
  variant: EntryRowVariant;
  testId: string;
}

export const EntryRow: React.FC<EntryRowProps> = ({
  entry,
  variant,
  testId,
}) => {
  const { t } = usePluginsTranslation();
  const arrow = entry.source === 'geoweb' ? '↓' : '↑';
  const tooltipTitle =
    variant === 'activity'
      ? t('plugin-debug-originated-from', { source: entry.source })
      : t('plugin-debug-source', { source: entry.source });

  const header = (
    <Box
      sx={{
        alignItems: 'center',
        display: 'flex',
        flexWrap: variant === 'recent' ? 'wrap' : undefined,
        gap: variant === 'activity' ? 1 : 0.5,
      }}
    >
      <Typography
        sx={{
          fontFamily: 'monospace',
          minWidth: variant === 'activity' ? 100 : 80,
        }}
        variant="caption"
      >
        {formatTime(entry.timestamp)}
      </Typography>
      <Chip
        color={typeColor(entry.type)}
        label={typeLabel(entry.type, t)}
        size="small"
        sx={{ fontSize: 10, height: 18 }}
      />
      {entry.type !== 'internal-state' && entry.scope ? (
        <Chip
          label={entry.scope}
          size="small"
          sx={{ fontSize: 10, height: 18 }}
          variant="outlined"
        />
      ) : null}
      <Tooltip title={tooltipTitle}>
        {variant === 'activity' ? (
          <Box sx={{ alignItems: 'center', display: 'flex' }}>
            <Typography component="span" sx={{ fontSize: 14, mr: 0.5 }}>
              {arrow}
            </Typography>
            <Typography
              sx={{
                color: sourceColor(entry.source),
                fontSize: 11,
                fontWeight: 600,
              }}
              variant="caption"
            >
              {entry.source}
            </Typography>
          </Box>
        ) : (
          <Typography
            sx={{
              color: sourceColor(entry.source),
              fontSize: 11,
              fontWeight: 600,
            }}
            variant="caption"
          >
            {arrow} {entry.source}
          </Typography>
        )}
      </Tooltip>
      {variant === 'recent' && (
        <Typography
          sx={{ color: 'text.secondary', fontSize: 11 }}
          variant="caption"
        >
          {entry.instanceId}
        </Typography>
      )}
    </Box>
  );

  const body = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
        width: '100%',
      }}
    >
      {header}
      <PreBox data={entry.data} />
    </Box>
  );

  if (variant === 'activity') {
    return (
      <ListItemButton
        data-testid={testId}
        disableRipple
        sx={{
          '&:hover': { bgcolor: 'action.hover' },
          alignItems: 'flex-start',
          cursor: 'default',
        }}
      >
        {body}
      </ListItemButton>
    );
  }

  return (
    <ListItem data-testid={testId} sx={{ alignItems: 'flex-start', px: 0 }}>
      {body}
    </ListItem>
  );
};
